/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { useState } from 'react';

import { useMutation } from '@apollo/client';
import { Switch } from '@coursera-web/cds-labs';
import {
  EnterpriseSetting_Key as EnterpriseSettingKey,
  EnterpriseSetting_ManagedLevelScopeType as ManagedLevelScopeType,
} from '__generated__/graphql-types';

import { Grid, InlineNotification, LoadingItem, Typography2 } from '@coursera/cds-core';

import { useEnrollment } from 'bundles/enterprise-admin-enrollment-setting/context/EnrollmentContextProvider';
import EnterpriseSettingUpdate from 'bundles/enterprise-admin-enrollment-setting/query/EnterpriseSettingUpdate.graphql';
import type {
  EnterpriseSettingUpdate_Dedupe1Mutation as EnterpriseSettingUpdateMutation,
  EnterpriseSettingUpdate_Dedupe1MutationVariables as EnterpriseSettingUpdateMutationVariables,
} from 'bundles/enterprise-admin-enrollment-setting/query/__generated__/EnterpriseSettingUpdate';
import { useThirdPartyOrganization } from 'bundles/enterprise-admin/ThirdPartyOrganizationProvider';
import { useTracker, useVisibilityTracker } from 'bundles/page/lib/event-pulse/react';

import _t from 'i18n!nls/enterprise-admin-enrollment-setting';

const styles = {
  header: css`
    color: var(--cds-color-grey-600);
    font-weight: bold;
    background-color: var(--cds-color-grey-25);
    border-bottom: 2px solid var(--cds-color-grey-50);
    padding: var(--cds-spacing-200) var(--cds-spacing-300);
    border-radius: var(--cds-border-radius-50) var(--cds-border-radius-50) 0 0;
  `,
  body: css`
    padding: var(--cds-spacing-400);
    width: 100%;
    display: block;
    border: 1px solid var(--cds-color-grey-50);
    border-top: none;
  `,
  cardContainer: css`
    width: 100%;
    margin-top: var(--cds-spacing-300);
  `,
  title: css`
    margin-bottom: var(--cds-spacing-100);
  `,
  container: css`
    margin: 0 auto var(--cds-spacing-400) auto;
    width: 100%;
  `,
  errorNotification: css`
    margin-top: var(--cds-spacing-300);
    margin-bottom: var(--cds-spacing-300);
  `,
};

const EnrollmentSetting = () => {
  const { id: thirdPartyOrganizationId, slug: thirdPartyOrganizationSlug } = useThirdPartyOrganization();
  const { enrollmentEnabled, error, loading, setEnrollmentEnabled, setError, showNotification, setShowNotification } =
    useEnrollment();
  const [isLoadingOnUpdate, setIsLoadingOnUpdate] = useState<boolean>(false);
  const enterpriseProps = thirdPartyOrganizationSlug
    ? { enterprise: { organizationSlug: thirdPartyOrganizationSlug } }
    : {};
  const track = useTracker();
  const toggleRef: React.MutableRefObject<HTMLDivElement | null> = useVisibilityTracker('view_checkbox', {
    checkbox: {
      name: 'enterprise_global_auto_enrollment_toggle',
      checked: enrollmentEnabled,
    },
    ...enterpriseProps,
  });
  const [updateMutation] = useMutation<EnterpriseSettingUpdateMutation, EnterpriseSettingUpdateMutationVariables>(
    EnterpriseSettingUpdate,
    {
      context: {
        clientName: 'gatewayGql',
      },
    }
  );

  const onToggleChange = async (isSelected: boolean) => {
    track('click_checkbox', {
      checkbox: {
        name: 'enterprise_global_auto_enrollment_toggle',
        checked: isSelected,
      },
      ...enterpriseProps,
    });
    setIsLoadingOnUpdate(true);
    try {
      const { data: updateData, errors: updateErrors } = await updateMutation({
        variables: {
          enterpriseSettingType: {
            booleanEnterpriseSetting: isSelected,
          },
          organizationId: thirdPartyOrganizationId,
          settingId: {
            managedLevelScope: {
              scope: ManagedLevelScopeType.ThirdPartyOrganization,
              value: thirdPartyOrganizationId,
            },
            enterpriseSettingKey: EnterpriseSettingKey.EnterpriseSettingKeyLmsLxpAutoEnrollmentEnabled,
          },
        },
      });
      if (updateData) {
        setEnrollmentEnabled(isSelected);
        setError({ ...error, mutationError: false });
        setIsLoadingOnUpdate(false);
        setShowNotification(false);
      } else if (updateErrors) {
        setEnrollmentEnabled(!isSelected);
        setError({ ...error, mutationError: true });
        setIsLoadingOnUpdate(false);
        setShowNotification(true);
      }
    } catch {
      setIsLoadingOnUpdate(false);
      setEnrollmentEnabled(!isSelected);
      setError({ ...error, mutationError: true });
      setShowNotification(true);
    }
  };

  const errorNotification = () => {
    let errorMessage = '';
    if (error.mutationError) {
      errorMessage = _t('Failed to update auto-enroll toggle. Please try again.');
    } else if (error.queryError) {
      errorMessage = _t("We couldn't retrieve your auto-enrollment sync status. Please reload the page or try again.");
    }

    return (
      <Grid item md={12} xs={12}>
        <InlineNotification
          severity="error"
          data-testid="enrollment-error-notification"
          onDismiss={() => {
            setShowNotification(false);
          }}
        >
          <Typography2 component="p" variant="bodyPrimary">
            {errorMessage}
          </Typography2>
        </InlineNotification>
      </Grid>
    );
  };

  return (
    <Grid
      container
      spacing={8}
      md={10}
      justifyContent="center"
      alignItems="center"
      data-testid="enrollment-setting-section"
      css={styles.container}
    >
      <Grid item md={12} xs={12}>
        <Typography2 component="h2" css={styles.title} variant="titleMedium">
          {_t('Auto-Enrollment for LMS/LXP learners')}
        </Typography2>
        <Typography2 component="p" color="supportText">
          {_t('Enroll your learners as soon as they start a course from your LMS or LXP platform.')}
        </Typography2>
      </Grid>

      {(error.queryError || error.mutationError) && showNotification && errorNotification()}
      <Grid container css={styles.cardContainer}>
        <Grid container css={styles.header} justifyContent="space-between" alignItems="center">
          <Typography2 variant="subtitleMedium" component="b" color="supportText">
            {_t('Auto-Enrollment for LMS/LXP learners')}
          </Typography2>
          <Grid item xs={1} md={1}>
            {loading || isLoadingOnUpdate ? (
              <LoadingItem label="" />
            ) : (
              <Grid ref={toggleRef}>
                <Switch selected={enrollmentEnabled} disabled={error.queryError} onToggle={onToggleChange} />
              </Grid>
            )}
          </Grid>
        </Grid>

        <Grid css={styles.body} container>
          <Grid container md={12} xs={12} justifyContent="space-between" alignItems="center">
            <Grid item xs={11} md={11}>
              <Typography2 variant="subtitleMedium" component="b">
                {_t('Enable Auto-Enrollment')}
              </Typography2>

              <Typography2 component="p">
                {_t(
                  "Once you're set up, students can simply click 'Start' or 'Enroll' within your LMS or LXP to get started right away, bypassing any unnecessary steps and getting straight to learning."
                )}
              </Typography2>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default EnrollmentSetting;
